import Honeybadger from '@honeybadger-io/js';

import { goToLogin, goToCurrentDashboard } from '/src/utils/auth';
import { clearCookies } from '/src/utils/cookies';
import { selectivelyClearLocalStorage } from '/src/utils/storage';
import { identifyUser, clearContext as clearHoneybadgerCtx } from '/src/utils/honeybadger';
import { setStoredAccessToken, resolveTokenRefresh, rejectTokenRefresh } from '/src/services/token';

function getErrorMessage(error) {
  return error?.graphQLErrors?.[0]?.originalError?.message || error?.message || '';
}

function clearSessionState() {
  clearHoneybadgerCtx();
  clearCookies();
  selectivelyClearLocalStorage();
  window.sessionStorage.clear();
}

export function handleFetchedToken(tokenPayload) {
  setStoredAccessToken(tokenPayload?.access_token);
  resolveTokenRefresh(tokenPayload);
}

export function handleAppAuthenticatedEntry(context) {
  identifyUser({
    userId: context.currentUser.userId,
    guestId: context.currentUser.guestId,
    email: context.currentUser.email,
    currentAccount: context.currentAccount,
  });

  if (window.CE2?.identify) {
    try {
      window.CE2.identify(context.currentUser.email);
    } catch (error) {
      Honeybadger.notify('User script failed to identify during app startup', {
        fingerprint: 'ce2-identify-startup-failed-immediate',
        context: {
          error: getErrorMessage(error),
          stack: error?.stack,
          email: context.currentUser.email,
          ce2_loaded: Boolean(window.CE2),
          ce2_identify_type: typeof window.CE2?.identify,
        },
      });
    }
    return;
  }

  window.CE_READY = function () {
    try {
      window.CE2.identify(context.currentUser.email);
    } catch (error) {
      Honeybadger.notify('User script failed to identify during app startup (deferred)', {
        fingerprint: 'ce2-identify-startup-failed-deferred',
        context: {
          error: getErrorMessage(error),
          stack: error?.stack,
          email: context.currentUser.email,
          ce2_loaded: Boolean(window.CE2),
          ce2_identify_type: typeof window.CE2?.identify,
        },
      });
    }
  };
}

export function handleAccountSwitchSuccess(token) {
  setStoredAccessToken(token);
  resolveTokenRefresh({ access_token: token });
  window.accountChannel?.postMessage?.({
    message: 'accountSwitch',
  });
  goToCurrentDashboard();
}

export function handleUnauthenticatedEntry() {
  rejectTokenRefresh(new Error('User is unauthenticated'));
  clearSessionState();
  window.accountChannel?.postMessage?.({
    message: 'unauthenticated',
  });
  goToLogin(true);
}

export function handleLoggedOutEntry(onSignOut) {
  rejectTokenRefresh(new Error('User logged out'));
  clearSessionState();
  window.accountChannel?.postMessage?.({
    message: 'logout',
  });
  onSignOut();
}

export function handleAccountErrorEntry() {
  rejectTokenRefresh(new Error('No accounts available for this session'));
}

export function handleMachineErrorEntry() {
  rejectTokenRefresh(new Error('Auth flow failed'));
}

export function setSharingSessionToken(token) {
  window.shr = token;
}

export function handleSharedResourcePresented(token) {
  setSharingSessionToken(token);
  resolveTokenRefresh(token);
}

export function handleSharedResourceNotFound() {
  rejectTokenRefresh(new Error('Shared resource not found'));
}

export function redirectToSharedResource(sharedResource) {
  const destination = new URL(sharedResource.resource.url, window.location.origin);
  const currentSearchParams = new URLSearchParams(window.location.search);

  currentSearchParams.forEach((value, key) => {
    destination.searchParams.append(key, value);
  });

  window.location.href = destination.toString();
}
